'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import { createWaitlistSubmissionResilient } from '@/utils/waitlistManagement';
import { WaitlistFormData } from '@/types/waitlist';
import styles from './waitlist.module.css';
import { trackEvent } from '@/lib/analytics/firebaseAnalytics';
import { AnalyticsEvents } from '@/lib/analytics/events';
import { queueSafeAnalytics, trackMetaFunnelEvent } from '@/lib/analytics/metaFunnel.client';
import { metaEmailEventId } from '@/lib/analytics/metaStandardEvents';

function readWaitlistFormData(form: HTMLFormElement): WaitlistFormData {
  const data = new FormData(form);
  return {
    fullName: String(data.get('fullName') ?? ''),
    email: String(data.get('email') ?? ''),
    instagram: String(data.get('instagram') ?? ''),
  };
}

function validateWaitlistForm(formData: WaitlistFormData): { [key: string]: string } {
  const newErrors: { [key: string]: string } = {};

  if (!formData.fullName.trim()) {
    newErrors.fullName = 'Full name is required';
  }

  if (!formData.email.trim()) {
    newErrors.email = 'Email is required';
  } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
    newErrors.email = 'Please enter a valid email address';
  }

  if (!formData.instagram.trim()) {
    newErrors.instagram = 'Instagram handle or link is required';
  } else {
    const input = formData.instagram.trim();
    const handleRegex = /^@[a-zA-Z0-9._]+$/;
    const linkRegex = /^(https?:\/\/)?(www\.)?instagram\.com\/[a-zA-Z0-9._]+\/?$/;

    if (!handleRegex.test(input) && !linkRegex.test(input)) {
      newErrors.instagram = 'Please enter a valid Instagram handle (e.g. @username) or link';
    }
  }

  return newErrors;
}

export default function OnlineWaitlistPage() {
  const router = useRouter();
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [submitting, setSubmitting] = useState(false);

  // Set page title
  useEffect(() => {
    document.title = 'Join Waitlist - Public';
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = readWaitlistFormData(e.currentTarget);
    const newErrors = validateWaitlistForm(formData);
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setSubmitting(true);
    try {
      await createWaitlistSubmissionResilient(formData);
    } catch (error) {
      console.error('Error submitting waitlist form:', error);
      setErrors({ submit: 'An error occurred. Please try again.' });
      setSubmitting(false);
      return;
    }
    router.push('/online-waitlist/thank-you');
    queueSafeAnalytics(() => {
      trackEvent(AnalyticsEvents.WAITLIST_SUBMISSION_SUCCESS, {
        email_domain: formData.email.split('@')[1],
      });
      trackMetaFunnelEvent({
        eventName: 'Lead',
        eventId: metaEmailEventId('Lead', 'waitlist', formData.email),
        contentName: 'online_waitlist',
        user: { email: formData.email, firstName: formData.fullName.split(/\s+/)[0] },
      });
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name } = e.target;
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <div className={styles.waitlistPage}>
      <div className={styles.container}>
        <div className={styles.formCard}>
          <div className={styles.header}>
            <Image 
              src="/LM_B_1024.png"
              alt="Lior Murphy"
              width={120}
              height={60}
              className={styles.logo}
              style={{ width: 'auto', height: 'auto' }}
              priority
            />
            <h1>Join the Waiting List</h1>
            <p className={styles.subtitle}>
              If you want a live Zoom seat, leave your details. I will write when a place opens.
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            action="/online-waitlist/thank-you"
            method="get"
            className={styles.form}
          >
            <div className={styles.formGroup}>
              <label htmlFor="fullName">Full Name *</label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                onChange={handleChange}
                className={errors.fullName ? styles.inputError : ''}
              />
              {errors.fullName && (
                <span className={styles.error}>{errors.fullName}</span>
              )}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="email">Email Address *</label>
              <input
                type="email"
                id="email"
                name="email"
                onChange={handleChange}
                className={errors.email ? styles.inputError : ''}
              />
              {errors.email && (
                <span className={styles.error}>{errors.email}</span>
              )}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="instagram">Instagram Handle or Link *</label>
              <input
                type="text"
                id="instagram"
                name="instagram"
                onChange={handleChange}
                placeholder="@your_handle or instagram.com/your_handle"
                className={errors.instagram ? styles.inputError : ''}
              />
              {errors.instagram && (
                <span className={styles.error}>{errors.instagram}</span>
              )}
            </div>

            {errors.submit && (
              <div className={styles.submitError}>{errors.submit}</div>
            )}

            <button 
              type="submit" 
              className={styles.submitBtn}
              disabled={submitting}
            >
              {submitting ? 'Submitting...' : 'Join Waiting List'}
            </button>
          </form>

          <div className={styles.footer}>
            <a href="/" className={styles.backLink}>← Back to Home</a>
          </div>
        </div>
      </div>
    </div>
  );
}
